import PropTypes from "prop-types";
import range from "lodash/range";
import cn from "classnames";
import { ChevronLeft, ChevronRight } from "@icons";

import { tagular } from "@cohesion/tagular";

export const createArray = (page, totalPages, minimum = 7) => {
  // this is an easy case, handle it first
  // if there are minimum or less pages, show all buttons
  if (totalPages <= minimum) {
    return range(1, totalPages + 1);
  }

  // default for 1,2,3
  let numbers = range(1, 4);

  if (page > 2) {
    // for higher numbers, start with an array with 1
    numbers = [1];
    // use number 2 rather than ellipsis for this one case
    if (page === 4) {
      numbers.push(2);
    }
    // add the ellipsis for even higher numbers
    else if (page > 4) {
      numbers.push("…");
    }

    // special range of four numbers if we are on last page
    if (page === totalPages) {
      // can safely return here
      return numbers.concat(range(totalPages - 2, totalPages + 1));
    }

    // otherwise, for a number like 7, this returns [5,6,7,8]
    numbers = numbers.concat(range(page - 1, page + 2));
  }

  // push ellipsis and final page number
  if (page < totalPages - 1) {
    if (page < totalPages - 3) {
      numbers.push("…");
    } else if (page === totalPages - 3) {
      // use penultimate page rather than ellipsis
      numbers.push(totalPages - 1);
    }

    numbers.push(totalPages);
  }

  return numbers;
};

function DirectionPill({
  className = "",
  direction = "prev",
  disabled = false,
  href,
}) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  const linkClasses = cn(
    "p-3 font-semibold block rounded-pill border border-black-200 capitalize cursor-pointer transform duration-300 hover:bg-black-300 hover:text-white hover:border-black-300",
    {
      "opacity-25 cursor-default hover:text-black hover:opacity-25 hover:bg-white hover:border-black-200":
        disabled,
    }
  );
  return (
    <div className={cn("relative", { disabled }, className)}>
      {disabled ? (
        <a className={linkClasses}>
          <Icon height="10" width="10" />
        </a>
      ) : (
        <a
          href={href}
          className={linkClasses}
          onClick={() => {
            tagular("click", {
              actionOutcome: "INTERNALLINK",
              outboundUrl: href,
              webElement: {
                name: "PAGINATION ARROW",
                location: "SECTION",
                elementType: "BUTTON",
                text: `${direction} ARROW`,
              },
            });
          }}
        >
          <Icon height="10" width="10" />
        </a>
      )}
    </div>
  );
}

DirectionPill.propTypes = {
  className: PropTypes.string,
  direction: PropTypes.oneOf(["prev", "next"]),
  disabled: PropTypes.bool,
  href: PropTypes.string.isRequired,
};

function Pagination({
  className = "",
  params = {},
  selectedPage,
  numPages,
  formSlug,
}) {
  const urlParams = page => new URLSearchParams({ ...params, page }).toString();
  const minimum = 7;
  return (
    <div
      className={cn(
        "flex flex-wrap flex-row items-start justify-center",
        className
      )}
    >
      <DirectionPill
        className="pr-2"
        direction="prev"
        disabled={selectedPage === 1}
        href={`${formSlug}?${urlParams(selectedPage - 1)}`}
      />
      <ul className="flex justify-center items-baseline">
        {createArray(selectedPage, numPages, minimum).map((page, index) => {
          const isEllipsis = page === "…";
          const key = `${page}-${index}`;
          const active = page === selectedPage;
          const handlePaginationEvents = () => {
            tagular("click", {
              actionOutcome: "INTERNALLINK",
              outboundUrl: `${formSlug}?${urlParams(page)}`,
              webElement: {
                location: "SECTION",
                elementType: "BUTTON",
                position: "PAGINATION NUMBER",
                text: `${page}`,
              },
            });
          };

          return (
            <li
              className={cn("inline-block mb-2", {
                "ml-1 md:ml-2": index > 0,
              })}
              key={key}
            >
              {isEllipsis ? (
                <span>{page}</span>
              ) : (
                <a
                  href={`${formSlug}?${urlParams(page)}`}
                  className={cn(
                    "block font-semibold text-xs rounded-pill border border-black-200 capitalize transform duration-300 py-2 px-3 sm:px-4 hover:bg-black-300 hover:text-white hover:border-black-300",
                    {
                      active,
                      "bg-black-300 text-white border-black-300 cursor-default":
                        active,
                    }
                  )}
                  onClick={handlePaginationEvents}
                >
                  {page}
                </a>
              )}
            </li>
          );
        })}
      </ul>
      <DirectionPill
        className="pl-2"
        direction="next"
        disabled={selectedPage === numPages}
        href={`${formSlug}?${urlParams(selectedPage + 1)}`}
      />
    </div>
  );
}

Pagination.propTypes = {
  selectedPage: PropTypes.number.isRequired,
  numPages: PropTypes.number.isRequired,
  className: PropTypes.string,
  params: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  formSlug: PropTypes.string.isRequired,
};

export { Pagination };
